import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSForm,
  WSLoader,
  WSSelect
} from "@wingspanhq/fe-component-library";
import { ReferenceDataRefreshRequestCollectionsInner } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import {
  queriesAccountingConnections,
  queriesAccountingReferenceData,
  queriesPayerEngagements,
  queriesPayers
} from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { selectorAccountingCustomerOptions } from "../../../Integrations/selectors/selectorAccountingCustomerOptions";
import { selectorAccountingItemOptions } from "../../../Integrations/selectors/selectorAccountingItemOptions";
import { selectorQuickbooksConnection } from "../../../Integrations/selectors/selectorQuickbooksConnection";

type Props = {
  payerId: string;
  engagementId: string | undefined;
  itemId: string | undefined;
  customerId: string | undefined;
  onClose: () => void;
};

type FormData = {
  qboCustomerId?: string;
  qboItemId?: string;
};

const EditPayerIntegrationModal: React.FC<Props> = ({
  onClose,
  payerId,
  engagementId,
  itemId,
  customerId
}) => {
  const { openSnackbar } = useWSSnackbar();

  const queryConnections =
    queriesAccountingConnections.listAccountingConnections.useQuery({});
  const connection = selectorQuickbooksConnection(queryConnections.data);
  const connectionId = connection?.id;

  const queryCustomers =
    queriesAccountingReferenceData.listAccountingCustomers.useQueryAll(
      { connectionId: connectionId as string },
      { enabled: !!connectionId }
    );
  const queryItems =
    queriesAccountingReferenceData.listAccountingItems.useQueryAll(
      { connectionId: connectionId as string },
      { enabled: !!connectionId }
    );

  // The mapping lives on the assignment when one is in context and on the client
  // itself otherwise — both are first-class v3 fields
  // (`PayerEngagementUpdate` / `PayerUpdate`), so both branches write directly.
  const [updateEngagement, updateEngagementMeta] =
    queriesPayerEngagements.updatePayerEngagement.useMutation();
  const [updatePayer, updatePayerMeta] =
    queriesPayers.updatePayer.useMutation();

  // v3 replacement for the v1 resync modal: the refresh is an async operation, so
  // it is fire-and-report rather than something to await a fresh list on.
  const [refreshReferenceData] =
    queriesAccountingReferenceData.refreshReferenceData.useMutation();

  const handleResync = (
    collection: ReferenceDataRefreshRequestCollectionsInner
  ) => {
    if (!connectionId) {
      return;
    }

    refreshReferenceData(
      {
        connectionId,
        referenceDataRefreshRequest: { collections: [collection] }
      },
      {
        onSuccess: () => {
          openSnackbar({
            type: "success",
            duration: 5000,
            message: `Refreshing ${collection.toLowerCase()} from QuickBooks. This can take a moment.`
          });
        },
        onError: () => {
          openSnackbar({
            type: "warning",
            duration: 5000,
            message: `Failed to refresh ${collection.toLowerCase()}`
          });
        }
      }
    );
  };

  if (
    queryConnections.isLoading ||
    queryCustomers.isLoading ||
    queryItems.isLoading
  ) {
    return <WSLoader.Spinner size="M" />;
  }

  const customerOptions = selectorAccountingCustomerOptions(
    queryCustomers.flatData
  );
  const itemOptions = selectorAccountingItemOptions(queryItems.flatData);
  const defaultItem = (queryItems.flatData ?? []).find(
    item => item.id === connection?.mappingDefaults?.defaultItemId
  );

  const handleSubmit = (values: FormData) => {
    const onSuccess = () => {
      onClose();
    };

    // A cleared select un-maps, which the API expresses as an explicit `null`;
    // `undefined` would be omitted from the PATCH and silently keep the old
    // mapping. Both update models type these as `string`, so widen once here —
    // same boundary cast `QuickbooksSetupStep4` makes.
    const mapping = {
      quickbooksCustomerId: values.qboCustomerId || null,
      quickbooksItemId: values.qboItemId || null
    } as unknown as {
      quickbooksCustomerId?: string;
      quickbooksItemId?: string;
    };

    if (engagementId) {
      updateEngagement(
        {
          payerId,
          payerEngagementId: engagementId,
          payerEngagementUpdate: mapping
        },
        { onSuccess }
      );
      return;
    }

    updatePayer({ payerId, payerUpdate: mapping }, { onSuccess });
  };

  const meta = engagementId ? updateEngagementMeta : updatePayerMeta;

  return (
    <WSForm<FormData>
      defaultValues={{ qboCustomerId: customerId, qboItemId: itemId }}
      onSubmit={handleSubmit}
    >
      <WSForm.Field
        mb="XL"
        name="qboCustomerId"
        component={WSSelect}
        componentProps={{
          placeholder: "Not mapped, create new Customer",
          options: customerOptions,
          action: {
            text: "Resync Customers",
            onClick: () =>
              handleResync(
                ReferenceDataRefreshRequestCollectionsInner.CUSTOMERS
              )
          }
        }}
        label="QBO Customer"
      />
      <WSForm.Field
        mb="2XL"
        name="qboItemId"
        component={WSSelect}
        componentProps={{
          options: itemOptions,
          placeholder: defaultItem
            ? `Use default "${defaultItem.displayName}"`
            : "Not mapped",
          action: {
            text: "Resync Items",
            onClick: () =>
              handleResync(ReferenceDataRefreshRequestCollectionsInner.ITEMS)
          }
        }}
        label="Default QBO Item"
      />

      <ErrorAlert mb="XL" error={meta.error} context="EditClientIntegrations" />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Update",
            kind: "Primary",
            type: "submit",
            loading: meta.isLoading
          },
          {
            kind: "Secondary",
            label: "Cancel",
            onClick: onClose
          }
        ]}
      />
    </WSForm>
  );
};

export const useEditPayerIntegrationModal = () =>
  useWSModal(EditPayerIntegrationModal, {
    title: "Edit integration",
    size: "S"
  });
